import { useEffect, useState } from "react"
import styled from "styled-components"

import { useAuth } from "context/AuthContext"
import { InputSignUp } from "ui/base/InputSignUp/InputSignUp"
import {
  filterOnlyEnglishCharacter,
  numberOnly,
  removeEmojis,
  safeLocalStorage,
  specialCharacterRemove,
} from "utils"

import { useMutation, useQueryClient } from "@tanstack/react-query"
import { addPaymentMethod } from "api/teacher.api.ts"
import {
  teacherLocalKey,
  teacherStepLocalKey,
} from "context/TeacherCreationContext.jsx"
import { routesPath } from "conts/routes.js"
import CryptoJS from "crypto-js"
import { useToast } from "hooks"
import { appRoute } from "pages/app/app.routes"
import { useNavigate } from "react-router-dom"
import { ButtonPrimary } from "ui/base/Button/ButtonStyled"
import Checkbox from "ui/base/checkbox/Checkbox"
import { HelperText } from "ui/base/helperText/HelperText"
import { hideScrollBar } from "ui/base/scrollbarStyle/scrollbarStyle"
import { ProfileIcon } from "ui/icons/index.js"
import { pixelTrackEvent } from "utils/facebookPixel.js"

export const PayoutMethod = () => {
  const queryClient = useQueryClient()
  const navigate = useNavigate()

  const { userData } = useAuth()
  const payoutInfo = userData?.teacher?.payment_info || []
  const userEmail = userData?.email || ""

  const numberList =
    payoutInfo.map((card) => {
      const bytes1 = CryptoJS.AES.decrypt(card.routing_number, userEmail)
      const bytes2 = CryptoJS.AES.decrypt(card.account_number, userEmail)
      let routing_number = bytes1.toString(CryptoJS.enc.Utf8)
      let account_number = bytes2.toString(CryptoJS.enc.Utf8)

      return {
        routing_number,
        account_number,
      }
    }) || []

  const [termsAndCondition, setTermsAndCondition] = useState(false)
  const [stripeAgreement, setStripeAgreement] = useState(false)

  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    routing_number: "",
    account_number: "",
    confirm_account_number: "",
  })

  const [formError, setFormError] = useState({
    first_name: "",
    last_name: "",
    routing_number: "",
    account_number: "",
    confirm_account_number: "",
  })

  const onFormChange = (key: string, value: string) => {
    const isError = value?.trim() === "" || value === undefined

    if (key === "first_name" || key === "last_name") {
      setFormData((prevState) => ({
        ...prevState,
        [key]: filterOnlyEnglishCharacter(removeEmojis(value)),
      }))
    } else {
      setFormData((prevState) => ({
        ...prevState,
        [key]: specialCharacterRemove(removeEmojis(value)),
      }))
    }

    if (key === "account_number" || key === "confirm_account_number") {
      setFormError((prevState) => ({
        ...prevState,
        account_number: "",
        confirm_account_number: "",
      }))
    }

    setFormError((prevState) => ({
      ...prevState,
      [key]: isError ? "Must be filled" : "",
    }))
  }

  const { mutate, isLoading, isSuccess } = useMutation({
    mutationFn: addPaymentMethod,
    onError: (err) => {
      useToast(
        "error",
        err?.response?.data?.message || "Error: Add payout method"
      )
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["userDetail"] })
    },
  })

  const onSubmit = () => {
    let isError = false
    const allEntires = Object.entries(formData)
    allEntires.map((item) => {
      if (item[1].trim().length === 0) {
        isError = true
        setFormError((prevState) => ({
          ...prevState,
          [item[0]]: "Must be filled",
        }))
      }
    })

    if (
      Array.isArray(numberList) &&
      numberList.find((it) => it?.account_number === formData.account_number)
    ) {
      isError = true
      useToast("error", "This account number has already been added")
      return
    } else if (formData.account_number !== formData.confirm_account_number) {
      isError = true
      useToast("error", "Account numbers must match")
      setFormError((prevState) => ({
        ...prevState,
        account_number: "Must be same",
        confirm_account_number: "Must be same",
      }))
      return
    }
    if (!termsAndCondition) {
      useToast("warn", "Accept Teache Instructor Terms")
      return
    } else if (!stripeAgreement) {
      useToast("warn", "Accept Stripe Recipient Agreement")
      return
    }

    if (isError) {
      return
    }

    mutate({ ...formData, email: userEmail })
  }

  useEffect(() => {
    if (isSuccess) {
      safeLocalStorage.removeItem(teacherLocalKey)
      safeLocalStorage.removeItem(teacherStepLocalKey)
      navigate(appRoute.welcome.fullPath)
    }
  }, [isSuccess])

  useEffect(() => {
    pixelTrackEvent("Enter Instructor Sign-up Page 6")
  }, [])

  return (
    <Container>
      <UserInfoOuterContainer>
        <UserInfoInnerContainer>
          <HelperText style={{ gridColumn: "span 2" }}>
            <HelperText> Add your bank account information</HelperText>
            <br />
            <HelperText>
              We process direct deposits for your payouts after the class.
              Direct deposits are managed and secured by Stripe.
            </HelperText>
          </HelperText>
          <InputSignUp
            id='firstName'
            placeholder='First name'
            icon={<ProfileIcon />}
            value={formData.first_name}
            setValue={(input) => onFormChange("first_name", input)}
            isError={formError.first_name}
            errorText={formError.first_name}
            maxLength={20}
          />
          <InputSignUp
            id='lastName'
            placeholder='Last name'
            icon={<ProfileIcon />}
            value={formData.last_name}
            setValue={(input) => onFormChange("last_name", input)}
            isError={formError.last_name}
            errorText={formError.last_name}
            maxLength={20}
          />
        </UserInfoInnerContainer>

        <HelperText color='placeholder'>
          Enter the account holder name exactly as it appears on bank statements
        </HelperText>
      </UserInfoOuterContainer>
      <AccountNumberContainer>
        <InputSignUp
          onInput={numberOnly}
          placeholder='Routing number'
          value={formData.routing_number}
          setValue={(input) => onFormChange("routing_number", input)}
          isError={formError.routing_number}
          errorText={formError.routing_number}
          maxLength={20}
        />
        <InputSignUp
          onInput={numberOnly}
          placeholder='Account number'
          value={formData.account_number}
          setValue={(input) => onFormChange("account_number", input)}
          isError={formError.account_number}
          errorText={formError.account_number}
          maxLength={20}
        />
        <InputSignUp
          onInput={numberOnly}
          placeholder='Confirm account number'
          value={formData.confirm_account_number}
          setValue={(input) => onFormChange("confirm_account_number", input)}
          isError={formError.confirm_account_number}
          errorText={formError.confirm_account_number}
          maxLength={20}
        />
      </AccountNumberContainer>

      <TermsContainer hideOnDesktop>
        <Checkbox
          label='Accept Teache Instructor'
          link={routesPath.terms + "?mode=app"}
          linkLabel='Terms'
          checked={termsAndCondition}
          onChange={(e) => setTermsAndCondition(e.target.checked)}
        />
        <Checkbox
          label='Accept'
          link={routesPath.stripeTerms + "?mode=app"}
          linkLabel='Stripe Recipient Agreement'
          checked={stripeAgreement}
          onChange={(e) => setStripeAgreement(e.target.checked)}
        />
      </TermsContainer>
      <TermsContainer hideOnMobile>
        <Checkbox
          label='Accept Teache Instructor'
          link={routesPath.terms}
          linkLabel='Terms'
          checked={termsAndCondition}
          onChange={(e) => setTermsAndCondition(e.target.checked)}
        />
        <Checkbox
          label='Accept'
          link={routesPath.stripeTerms}
          linkLabel='Stripe Recipient Agreement'
          checked={stripeAgreement}
          onChange={(e) => setStripeAgreement(e.target.checked)}
        />
      </TermsContainer>

      <ButtonPrimary
        onClick={() => {
          pixelTrackEvent("Instructor Sign-up Page 6: Clicked on Submit")
          onSubmit()
        }}
        disabled={isLoading}>
        Submit
      </ButtonPrimary>
    </Container>
  )
}

export const Container = styled.div`
  width: 100%;
  gap: 1rem;
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  overflow-y: auto;
  padding-top: 2rem;
  font-family: var(--font-secondary);
  ${hideScrollBar}
`

const UserInfoOuterContainer = styled.div`
  display: grid;
  gap: 0.3rem;
`
const UserInfoInnerContainer = styled.div`
  display: grid;
  gap: 20px;
  grid-template-columns: 1fr 1fr;
  align-items: start;
`

const AccountNumberContainer = styled.div`
  display: grid;
  gap: 1rem;
`
const RoutingNumberContainer = styled.div`
  display: grid;
  gap: 0.3rem;
`

const TermsContainer = styled.div`
  gap: 0.3rem;
  display: ${({ hideOnMobile }) => (hideOnMobile ? "none" : "grid")};
  @media (min-width: 768px) {
    display: ${({ hideOnDesktop }) => (hideOnDesktop ? "none" : "grid")};
  }
`
